import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import swal from "sweetalert";
import axios from "axios";
import MDBox from "admin/components/MDBox";
import MDTypography from "admin/components/MDTypography";
import MDInput from "admin/components/MDInput";
import DashboardLayout from "admin/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "admin/examples/Navbars/DashboardNavbar";

const API = process.env.REACT_APP_APIHOST || "http://localhost:3033";
const COUNTRIES = [
  // Active conflicts / High risk
  "Ukraine","Russia","Syria","Iraq","Yemen","Libya","Somalia","Sudan","South Sudan","Myanmar","Afghanistan","Palestine","Israel","Lebanon","Iran",
  // Elevated risk
  "Mali","Burkina Faso","Niger","Nigeria","Chad","DR Congo","Mozambique","Ethiopia","Eritrea","Central African Republic","Cameroon",
  // MENA
  "Egypt","Jordan","Turkey","Saudi Arabia","UAE","Kuwait","Bahrain","Qatar","Oman","Tunisia","Algeria","Morocco",
  // South / Central Asia
  "Pakistan","India","Bangladesh","Sri Lanka","Nepal","Tajikistan","Uzbekistan","Kyrgyzstan","Turkmenistan","Kazakhstan",
  // East Asia / Pacific
  "China","Taiwan","North Korea","South Korea","Japan","Philippines","Indonesia","Thailand",
  // Americas
  "Mexico","Colombia","Venezuela","Haiti","Ecuador","Honduras","El Salvador","Guatemala","Brazil","Peru",
  // Europe
  "Georgia","Moldova","Armenia","Azerbaijan","Serbia","Kosovo","Bosnia and Herzegovina",
];
const FREQUENCIES = [{ v: 'once', l: 'Once' }, { v: 'hourly', l: 'Every hour' }, { v: 'every3h', l: 'Every 3h' }, { v: 'every6h', l: 'Every 6h' }, { v: 'every12h', l: 'Every 12h' }, { v: 'daily', l: 'Daily' }];

function DataCollection() {
    const [ops, setOps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ countries: [], hoursBack: 24, dateTarget: '', frequency: 'once', autoPublish: false });
    const [excludedCountries, setExcludedCountries] = useState([]);
    const [excludeInput, setExcludeInput] = useState(null);

    const fetchExcluded = async () => {
        try { const r = await axios.get(API + "/crawl-ops/excluded-countries"); if (r.data.status) setExcludedCountries(r.data.data || []); } catch(e) {}
    };
    const addExcluded = async (country) => {
        if (!country || excludedCountries.includes(country)) return;
        await axios.post(API + "/crawl-ops/excluded-countries", { country });
        setExcludeInput(null);
        fetchExcluded();
    };
    const removeExcluded = async (country) => {
        await axios.delete(API + "/crawl-ops/excluded-countries/" + encodeURIComponent(country));
        fetchExcluded();
    };

    useEffect(() => { fetchOps(); fetchExcluded(); const t = setInterval(fetchOps, 10000); return () => clearInterval(t); }, []);

    const fetchOps = async () => {
        try { const r = await axios.get(API + "/crawl-ops"); if (r.data.status) setOps(r.data.data); } catch(e) {}
        finally { setLoading(false); }
    };

    const createOp = async () => {
        if (form.countries.length === 0) return;
        await axios.post(API + "/crawl-ops", form);
        setShowAdd(false);
        setForm({ countries: [], hoursBack: 24, dateTarget: '', frequency: 'once' });
        fetchOps();
    };

    const runNow = async (id) => {
        await axios.put(API + "/crawl-ops/" + id, { status: 'scheduled', nextRun: new Date().toISOString() });
        fetchOps();
    };

    const [editId, setEditId] = useState(null);

    const startEdit = (op) => {
        setEditId(op._id);
        setForm({ countries: op.countries || [], hoursBack: op.hoursBack || 24, dateTarget: op.dateTarget || '', frequency: op.frequency || 'once', autoPublish: op.autoPublish || false });
        setShowAdd(true);
    };

    const saveEdit = async () => {
        if (editId) {
            await axios.put(API + "/crawl-ops/" + editId, form);
            setEditId(null);
        } else {
            await axios.post(API + "/crawl-ops", form);
        }
        setShowAdd(false);
        setForm({ countries: [], hoursBack: 24, dateTarget: '', frequency: 'once', autoPublish: false });
        fetchOps();
    };

    const deleteOp = async (id) => {
        swal({ title: "Delete?", icon: "warning", buttons: true, dangerMode: true }).then(async (ok) => {
            if (ok) { await axios.delete(API + "/crawl-ops/" + id); fetchOps(); }
        });
    };

    const toggleOp = async (op) => {
        await axios.put(API + "/crawl-ops/" + op._id, { active: !op.active });
        fetchOps();
    };

    const statusColor = (s) => s === 'completed' ? '#4CAF50' : s === 'running' ? '#C29717' : s === 'failed' ? '#e55' : s === 'scheduled' ? '#7a9ec2' : '#888';
    const timeAgo = (d) => { if (!d) return ''; const m = Math.round((Date.now() - new Date(d).getTime()) / 60000); return m < 60 ? m + 'min ago' : Math.round(m/60) + 'h ago'; };
    const timeUntil = (d) => { if (!d) return ''; const m = Math.round((new Date(d).getTime() - Date.now()) / 60000); if (m < 0) return 'overdue'; return m < 60 ? 'in ' + m + 'min' : 'in ' + Math.round(m/60) + 'h'; };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={2} pb={2}>
                {/* Excluded countries */}
                <Card sx={{ mb: 2 }}>
                    <MDBox px={2} pt={1.5} pb={1.5}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <MDTypography variant="h6" fontWeight="medium" sx={{ color: "#fff !important", fontSize: "13px" }}>
                                <Icon sx={{ verticalAlign: "middle", mr: 0.5, color: "#e55", fontSize: "16px !important" }}>block</Icon>
                                Countries to exclude
                            </MDTypography>
                            <MDTypography variant="caption" sx={{ color: "#888 !important" }}>
                                Events with Side B matching these countries will be skipped during crawling
                            </MDTypography>
                        </MDBox>
                        <MDBox display="flex" gap={1} alignItems="center" mb={1}>
                            <Autocomplete
                                options={COUNTRIES.filter(c => !excludedCountries.includes(c))}
                                value={excludeInput}
                                onChange={(e, v) => setExcludeInput(v)}
                                size="small"
                                sx={{ width: 250 }}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder="Select country..." variant="outlined" size="small"
                                        sx={{ "& .MuiInputBase-root": { height: "32px", fontSize: "11px" } }} />
                                )}
                            />
                            <Icon onClick={() => addExcluded(excludeInput)}
                                sx={{ cursor: "pointer", color: "#C29717", fontSize: "20px !important", "&:hover": { color: "#fff" } }}>add_circle</Icon>
                        </MDBox>
                        <MDBox display="flex" gap={0.5} flexWrap="wrap">
                            {excludedCountries.map(c => (
                                <MDBox key={c} sx={{
                                    display: "inline-flex", alignItems: "center", gap: "4px",
                                    padding: "2px 10px", fontSize: "11px", color: "#e55",
                                    border: "1px solid rgba(220,50,50,0.25)", borderRadius: "10px",
                                    cursor: "pointer", "&:hover": { borderColor: "rgba(220,50,50,0.5)" }
                                }} onClick={() => removeExcluded(c)}>
                                    {c} <span style={{ opacity: 0.5, fontSize: "9px" }}>&times;</span>
                                </MDBox>
                            ))}
                            {excludedCountries.length === 0 && (
                                <MDTypography variant="caption" sx={{ color: "#555 !important" }}>No countries excluded</MDTypography>
                            )}
                        </MDBox>
                    </MDBox>
                </Card>

                <Card>
                    <MDBox px={2} pt={1} pb={0.5} display="flex" justifyContent="space-between" alignItems="center">
                        <MDTypography variant="body2" sx={{ color: "#C29717", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1.5px" }}>
                            Crawl operations ({ops.length})
                        </MDTypography>
                        <MDTypography component="span" sx={{ color: "#aaa", fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", "&:hover": { color: "#fff" } }} onClick={() => { setShowAdd(!showAdd); setEditId(null); setForm({countries:[],hoursBack:24,dateTarget:'',frequency:'once',autoPublish:false}) }}>
                            <Icon sx={{ fontSize: "14px !important" }}>add_circle_outline</Icon> New operation
                        </MDTypography>
                    </MDBox>

                    {showAdd && (
                        <MDBox px={2} py={1.5} sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                            <Grid container spacing={1.5}>
                                <Grid item xs={12}>
                                    <Autocomplete multiple freeSolo options={COUNTRIES} value={form.countries}
                                        onChange={(_, v) => setForm({...form, countries: v})}
                                        renderTags={(val, getTagProps) => val.map((co, i) => {
                                            const { onDelete } = getTagProps({ index: i });
                                            return <span key={co} style={{display:'inline-flex',alignItems:'center',gap:'4px',background:'rgba(194,151,23,0.15)',border:'1px solid rgba(194,151,23,0.3)',borderRadius:'2px',padding:'2px 8px',fontSize:'11px',color:'#fff',margin:'2px'}}>{co}<span style={{cursor:'pointer',color:'#C29717',fontSize:'14px'}} onClick={onDelete}>&times;</span></span>;
                                        })}
                                        renderInput={(params) => <TextField {...params} label="Target countries" variant="outlined" size="small" />} />
                                </Grid>
                                <Grid item xs={4}>
                                    <MDInput type="number" label="Hours back" fullWidth value={form.hoursBack} onChange={e => setForm({...form, hoursBack: parseInt(e.target.value) || 24})} />
                                </Grid>
                                <Grid item xs={4}>
                                    <MDInput type="text" label="Specific date (optional)" fullWidth value={form.dateTarget} onChange={e => setForm({...form, dateTarget: e.target.value})} placeholder="March 18 2026" />
                                </Grid>
                                <Grid item xs={4}>
                                    <MDInput select label="Frequency" fullWidth value={form.frequency} onChange={e => setForm({...form, frequency: e.target.value})}
                                        InputLabelProps={{ shrink: true }} InputProps={{ style: { height: "40px" } }} SelectProps={{ style: { height: "40px" } }} sx={{ height: "40px" }}>
                                        {FREQUENCIES.map(f => <MenuItem key={f.v} value={f.v}>{f.l}</MenuItem>)}
                                    </MDInput>
                                </Grid>
                                <Grid item xs={12}>
                                    <span onClick={() => setForm({...form, autoPublish: !form.autoPublish})} style={{display:'flex',alignItems:'center',gap:'6px',cursor:'pointer',fontSize:'11px',color:form.autoPublish?'#4CAF50':'#888',marginBottom:'8px'}}>
                                        <span style={{width:'14px',height:'14px',border:'1px solid '+(form.autoPublish?'#4CAF50':'rgba(255,255,255,0.2)'),borderRadius:'2px',display:'flex',alignItems:'center',justifyContent:'center',background:form.autoPublish?'rgba(76,175,80,0.15)':'transparent',fontSize:'9px'}}>{form.autoPublish ? '\u2713' : ''}</span>
                                        Auto-publish events
                                    </span>
                                </Grid>
                                <Grid item xs={12}>
                                    <MDBox display="flex" gap={1}>
                                        <span onClick={saveEdit} style={{padding:'5px 14px',background:'rgba(194,151,23,0.15)',border:'1px solid rgba(194,151,23,0.3)',borderRadius:'2px',color:'#C29717',fontSize:'11px',cursor:'pointer'}}>{editId ? 'Update' : 'Create'}</span>
                                        <span onClick={() => setShowAdd(false)} style={{padding:'5px 14px',border:'1px solid rgba(255,255,255,0.12)',borderRadius:'2px',color:'#888',fontSize:'11px',cursor:'pointer'}}>Cancel</span>
                                    </MDBox>
                                </Grid>
                            </Grid>
                        </MDBox>
                    )}

                    <MDBox px={2} py={1}>
                        {loading ? <MDBox display="flex" justifyContent="center" p={3}><div className="forte-spin" style={{margin:"0 auto"}}></div></MDBox>
                        : ops.length === 0 ? <MDTypography sx={{ color: "#888", fontSize: "13px", py: 2, textAlign: "center" }}>No operations</MDTypography>
                        : ops.map(op => (
                            <MDBox key={op._id} sx={{ borderBottom: "1px solid rgba(255,255,255,0.04)", py: 1.2, display: "flex", justifyContent: "space-between", alignItems: "flex-start", opacity: op.active ? 1 : 0.4 }}>
                                <MDBox>
                                    <MDBox display="flex" gap={0.5} flexWrap="wrap" mb={0.3}>
                                        {(op.countries || []).map(co => <span key={co} style={{fontSize:'10px',color:'#C29717',background:'rgba(194,151,23,0.08)',borderRadius:'2px',padding:'0 5px'}}>{co}</span>)}
                                    </MDBox>
                                    <MDBox display="flex" gap={1} alignItems="center" mb={0.3}>
                                        <span style={{width:'8px',height:'8px',borderRadius:'50%',background:statusColor(op.status)}}></span>
                                        <span style={{fontSize:'11px',color:statusColor(op.status),textTransform:'uppercase'}}>{op.status}</span>
                                        <span style={{fontSize:'10px',color:'#888'}}>{op.frequency !== 'once' ? op.frequency : ''}</span>
                                        <span style={{fontSize:'10px',color:'#666'}}>{op.hoursBack}h window</span>
                                        {op.autoPublish && <span style={{fontSize:'9px',color:'#4CAF50'}}>auto-publish</span>}
                                        {op.dateTarget && <span style={{fontSize:'10px',color:'#888'}}>{op.dateTarget}</span>}
                                    </MDBox>
                                    <MDBox display="flex" gap={1} fontSize="10px" color="#666">
                                        {op.nextRun && <span>Next: {timeUntil(op.nextRun)}</span>}
                                        {op.lastRun && <span>Crawled: {timeAgo(op.lastRun)}</span>}
                                        {op.resultsFound > 0 && <span style={{color:'#4CAF50'}}>Found: {op.resultsFound} | Inserted: {op.resultsInserted}</span>}
                                        {op.error && <span style={{color:'#e55'}}>{op.error.substring(0, 40)}</span>}
                                    </MDBox>
                                </MDBox>
                                <MDBox display="flex" gap={0.8} flexShrink={0} alignItems="center">
                                    <span onClick={() => runNow(op._id)} title="Run now" style={{color:'#C29717',cursor:'pointer',fontSize:'11px',border:'1px solid rgba(194,151,23,0.3)',borderRadius:'2px',padding:'1px 6px',background:'rgba(194,151,23,0.08)'}}>&#9654;</span>
                                    <span onClick={() => startEdit(op)} style={{color:'#888',cursor:'pointer',fontSize:'12px'}}>{'✎'}</span>
                                    <span onClick={() => toggleOp(op)} style={{width:'12px',height:'12px',borderRadius:'2px',border:'1px solid '+(op.active?'#4CAF50':'rgba(255,255,255,0.15)'),background:op.active?'rgba(76,175,80,0.15)':'transparent',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',fontSize:'8px',color:'#4CAF50'}}>{op.active?'\u2713':''}</span>
                                    <span onClick={() => deleteOp(op._id)} style={{color:'#888',cursor:'pointer',fontSize:'12px'}}>x</span>
                                </MDBox>
                            </MDBox>
                        ))}
                    </MDBox>
                </Card>
            </MDBox>
        </DashboardLayout>
    );
}
export default DataCollection;
